import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Helmet } from "react-helmet";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMemo } from 'react'
import { useEffect } from 'react';
import { useCallback } from 'react';
import { useState } from 'react'
import { Alert, Badge, Breadcrumb, Button, Card, Col, Container, Modal, Row, Table } from 'react-bootstrap'
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ModelDeploySettings, ModelDeploymentStatus, Models } from '../../api/Models';
import { useFullScreenLoader } from '../../contexts/fullScreenLoader';
import { useToasts } from '../../contexts/toasts';
import AlidaLoader from '../../components/common/AlidaLoader';
import AssetRibbon from '../../components/common/AssetRibbon';
import { formatDate, setObjectType } from '../../utils';
import FileSaver from 'file-saver';
import ScriptEditor from '../../components/common/ScriptEditor';
import { useInterval } from '../../utils/useInterval';
import { Flex, Form, Tag, Tooltip, Button as AntButton } from 'antd';
import { BdaModel } from '../../models/BdaModel';
import { DatabaseDatasource, ObjectStorageDatasource } from '../../models/Datasource';
import { DataRetriever } from '../../api/DataRetriever';
import { DataspaceConnector } from '../../models/DataSpace';
import DataspaceConnectorItem from '../../components/list-items/DataspaceConnectorItem';
import AssetProjectsEditor from '../../components/forms/AssetProjectsEditor';
import IconButton from '../../components/common/IconButton';
import { useSelector } from 'react-redux';
import { sectionLabelsSelector } from '../../slices/sectionsSlice';
import { RootState } from '../../store';
import AppTag from '../../components/common/AppTag';
import UserItem from '../../components/common/UserItem';
import AppLoader from '../../components/common/AppLoader';
import MDEditor from '@uiw/react-md-editor';
import { AccessLevel } from '../../models/generics';
import { useAuth } from '../../contexts/auth';

// const formLayout = {
//   labelCol: {
//     xs: 24,
//     md: 5,
//   },
//   wrapperCol: {
//     xs: 24,
//     md: 18,
//   },
// }


export default function ModelDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [model, setModel] = useState<BdaModel>();
  const [error, setError] = useState(false);
  const [modelDeploymentStatus, setModelDeploymentStatus] = useState<ModelDeploymentStatus>();
  const [modelMetadata, setModelMetadata] = useState<Record<string, any>>();
  const [forcePolling, setForcePolling] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [connectors, setConnectors] = useState<DataspaceConnector[]>();
  const [form] = Form.useForm();
  // const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showProjectsModal, setShowProjectsModal] = useState(false);
  const [modelCard, setModelCard] = useState<Record<string, any>>({});

  const { loggedUser } = useAuth();
  const { showToast } = useToasts();
  const { showLoader, hideLoader } = useFullScreenLoader();
  const servingLogicEnabled = useMemo(() => window.__env.MODEL_SERVING_ENABLED && model?.created && model.format !== null && !model.deleteDateTime, [model]);
  const predictionLink = useMemo(() => window.__env.MODEL_INFER_URL?.replace("$MODEL_ID", id!), [id]);
  const showActions = useMemo(() => model && ((model.accessLevel === AccessLevel.PRIVATE && model.owner.id === loggedUser?.sub) || (model.accessLevel === AccessLevel.TEAM && loggedUser?.userOrganizationStatus?.current.id)), [model, loggedUser]);

  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));
  const applicationLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "applications"));

  const initConnectors = useCallback(() => {
    const alidaUrl = window.location.href;

    DataRetriever.getDataspaceConnectorsByUrl(alidaUrl)
      .then(
        ({ data }) => setConnectors(data),
        (err) => {
          setConnectors([]);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('connectors-error', message, `${modelLabels.singular} Connectors Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [showToast, modelLabels]);

  const initModel = useCallback(() => {
    Models.getModel(+id!)
      .then(
        ({ data }) => {
          Models.getModelMarkdown(+id!)
            .then(
              ({ data: markdown }) => setModel({ ...data, markDown: { value: markdown } }),
              () => setModel(data),
            );
          if (window.__env.DATASPACES_ENABLED) initConnectors();
        },
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, initConnectors, showToast, modelLabels]);

  useEffect(() => {
    if (model && servingLogicEnabled && modelDeploymentStatus?.ready && !modelMetadata) {
      Models.getModelMetadata(+id!)
        .then(
          ({ data }) => setModelMetadata(data),
          (err) => {
            const message = err.response?.data?.message || err.message;
            const status = err.response?.status;
            showToast('model-metadata-error', message, `${modelLabels.singular} Metadata Error ${status ? `(${status})` : ''}`, 'danger');
          }
        );
    }
  }, [id, showToast, model, servingLogicEnabled, modelDeploymentStatus, modelMetadata, modelLabels]);

  const getModelStatus = useCallback(() => {
    Models.getModelStatus(+id!)
      .then(
        () => setModelDeploymentStatus({ label: 'Deployed', ready: true }),
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          if (status === 404) {
            setModelDeploymentStatus({ ready: false, label: "Not Deployed" });
            setForcePolling(false);
          }
          else showToast('model-status-error', message, `${modelLabels.singular} Status Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [id, showToast, modelLabels]);

  const retrieveModelCard = useCallback((download?: boolean) => {
    if (download) showLoader();

    const params = new URLSearchParams();
    if (download) params.append("asText", "true");
    params.append("layout", "hugging");

    Models.getModelCardInfo(model!.id, params)
      .then(
        ({ data }) => {
          if (download) {
            hideLoader();
            const blob = new Blob([data as string], { type: 'text/plain' });
            FileSaver.saveAs(blob, `${model!.name}.txt`);
          }
          else setModelCard(data as Record<string, any>);
        },
        (err) => {
          if (download) hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }, [model, modelLabels, showToast, showLoader, hideLoader]);

  useEffect(() => initModel(), [initModel]);

  useInterval(() => getModelStatus(), forcePolling ? 10000 : undefined);

  useEffect(() => {
    if (model) retrieveModelCard();
    if (model && servingLogicEnabled) getModelStatus();
    if (servingLogicEnabled) form.setFieldsValue({ replicasNumber: 1, cpuLimit: 200, memoryLimit: 256 });
  }, [servingLogicEnabled, model, getModelStatus, form, retrieveModelCard]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(predictionLink);
    showToast('text-copied', 'URL copied to clipboard!');
  }, [predictionLink, showToast]);

  const onEditModel = useCallback(() => navigate(`/models/${id}/edit`), [id, navigate]);
  const onDeleteModel = useCallback(() => setShowDeleteModal(true), []);
  const handleDeleteClose = useCallback(() => setShowDeleteModal(false), []);
  // const handleSettingsClose = useCallback(() => setShowSettingsModal(false), []);

  const deleteModel = () => {
    showLoader();

    Models.deleteModel(model!.id)
      .then(
        () => {
          showToast('model-delete-success', `${modelLabels.singular} deleted successfully.`, 'Success', 'success');
          hideLoader();
          handleDeleteClose();
          navigate('/models');
        },
        (err) => {
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const startModelDeployment = (settings?: ModelDeploySettings) => {
    showLoader();

    let body = undefined;
    if (settings) {
      body = { ...settings };
      body.cpuLimit += "m";
      body.memoryLimit += "Mi";
    }

    Models.deployModel(+id!, body)
      .then(
        () => {
          hideLoader();
          setForcePolling(true);
          // setShowSettingsModal(false);
          showToast('model-start-success', `${modelLabels.singular} deployment started successfully`, 'Model Success', 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const stopModelDeployment = () => {
    showLoader();

    Models.stopModel(+id!)
      .then(
        () => {
          hideLoader();
          showToast('model-stop-success', `${modelLabels.singular} deployment stopped successfully`, `${modelLabels.singular} Success`, 'success');
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('model-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const downloadModel = () => {
    showLoader();

    Models.downloadModel(model!.id)
      .then(
        ({ data: modelToDownload }) => {
          hideLoader();
          const blob = new Blob([modelToDownload], { type: 'application/zip' });
          FileSaver.saveAs(blob, `${model!.name}.zip`);
        },
        (err) => {
          hideLoader();
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.singular} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      )
  }

  const handleToggleClick = () => {
    if (modelDeploymentStatus!.ready) stopModelDeployment();
    // else setShowSettingsModal(true);
    else startModelDeployment();
  }

  const navigateToPublish = useCallback(() => navigate('publish'), [navigate]);
  const handleModelCardDownload = useCallback(() => retrieveModelCard(true), [retrieveModelCard]);
  const modelCardFieldsMissing = useMemo(() => modelCard && modelCard.missingFields && modelCard.missingFields.length > 0, [modelCard]);

  const openProjectsModal = () => setShowProjectsModal(true);
  const handleProjectsModalClose = () => setShowProjectsModal(false);

  const renderModelCardValue = useCallback((key: string, value: any) => {
    if (key === "languages") return value.filter((l: string) => l).map((lang: string) => <Tag key={lang} className="ms-1">{lang}</Tag>);
    if (key === "licenseLink") return <a href={value} target="_blank" rel="noopener noreferrer">{value}</a>;
    return typeof value === "object" ? <code>{JSON.stringify(value)}</code> : value;
  }, []);

  const renderAsyncContent = () => {
    if (error) return (
      <Alert variant="danger">
        <FontAwesomeIcon icon={solid('exclamation-triangle')} />
        <span className="ms-2">An error occurred. Please, try again later.</span>
      </Alert>
    )

    if (!model) return (
      <div className="d-flex justify-content-center py-5">
        <AlidaLoader size={300} />
      </div>
    )

    return (
      <>
        <Breadcrumb>
          <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/models' }}>{modelLabels.plural}</Breadcrumb.Item>
          <Breadcrumb.Item active>{model.name}</Breadcrumb.Item>
        </Breadcrumb>

        <Card className="item-detail-group main bg-model">
          <div className="item-ribbons">
            {(model.createdByServiceId === null) && (
              <AssetRibbon
                icon={solid('user')}
                color="info"
                tooltip="This has been created by a user."
              />
            )}
            {(model.createdByServiceId !== null) && (
              <AssetRibbon
                icon={solid('cogs')}
                color="dark"
                tooltip="This is an output from an execution."
              />
            )}
            {!model.created && (
              <AssetRibbon
                icon={solid('lock')}
                color="warning"
                tooltip="This has not been created yet."
              />
            )}
          </div>

          <FontAwesomeIcon className="item-icon" icon={solid('graduation-cap')} />

          <Flex justify="end">
            <Tag color="blue" className="ms-auto shadow text-uppercase font-xs fw-bold m-0">{model.accessLevel}</Tag>
            {model.deleteDateTime && (<Tag color="red" className="shadow text-uppercase font-xs fw-bold m-0 ms-2">Deprecated</Tag>)}
          </Flex>

          <div className="position-relative p-2 pt-4">
            <Row className="gx-0 align-items-center justify-content-md-between">
              <Col xs={12} md="auto" className="pe-3">
                <h3 className="mb-0">{model.name}</h3>
                <p className="text-trailing mb-3">{model.description}</p>
              </Col>

              <Col xs={12} md="auto" className="text-center text-md-start">
                {!model.deleteDateTime && (
                  <>
                    <Tooltip title="Manage Projects">
                      <button className="circle-button" onClick={openProjectsModal}>
                        <span className="fa-layers">
                          <FontAwesomeIcon icon={solid('folder')} transform="left-14 grow-2" />
                          <FontAwesomeIcon icon={solid('cog')} transform="shrink-4 down-8 right-3" />
                        </span>
                      </button>
                    </Tooltip>
                    {servingLogicEnabled && modelDeploymentStatus && (
                      <Tooltip title={`${modelDeploymentStatus.ready ? 'Stop' : 'Deploy'} ${modelLabels.singular}`}>
                        <button className="circle-button" onClick={handleToggleClick}>
                          <FontAwesomeIcon icon={modelDeploymentStatus.ready ? solid('pause') : solid('play')} />
                        </button>
                      </Tooltip>
                    )}
                    {window.__env.DATASPACES_ENABLED && (
                      <Tooltip title="Publish">
                        <button className="circle-button" onClick={navigateToPublish}>
                          <FontAwesomeIcon icon={solid('share-from-square')} />
                        </button>
                      </Tooltip>
                    )}
                    <Tooltip title="Download">
                      <button className="circle-button" disabled={!model.created} onClick={downloadModel}>
                        <FontAwesomeIcon icon={solid('download')} />
                      </button>
                    </Tooltip>
                    {showActions && (
                      <>
                        <Tooltip title="Edit">
                          <button className="circle-button" onClick={onEditModel}>
                            <FontAwesomeIcon icon={solid('pen')} />
                          </button>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <button className="circle-button" onClick={onDeleteModel}>
                            <FontAwesomeIcon icon={solid('trash')} />
                          </button>
                        </Tooltip>
                      </>
                    )}
                  </>
                )}
              </Col>
            </Row>
          </div>

          <Flex>
            <div className="bg-highlight">
              <UserItem className="negative" user={model.owner!} organization={model.organization!} />
            </div>
          </Flex>

          {model.tags.length > 0 && (
            <Flex className="tags-wrapper w-auto my-2">
              {model.tags.map(tag => <AppTag className="me-1" tag={tag} key={tag} />)}
            </Flex>
          )}

          <div className="item-dates">
            <div className="d-inline-block">
              <b>Created:</b>
              <span className="ms-1">{formatDate(model.createDateTime)}</span>
            </div>
            <div className="d-inline-block ms-2">
              <b>Last Update:</b>
              <span className="ms-1">{formatDate(model.updateDateTime)}</span>
            </div>
          </div>
        </Card>

        {servingLogicEnabled && (
          <Card className="item-detail-group mb-3">
            <div className="item-detail-highlight">
              <FontAwesomeIcon icon={solid('cube')} fixedWidth />
              <h5 className="d-inline ms-2 mb-0">{modelLabels.singular} Deployment</h5>
            </div>

            <Card.Body>
              <Table className="mb-0">
                <thead>
                  <tr>
                    <th className="border-0 p-0" style={{ width: 200 }}></th>
                    <th className="border-0 p-0"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="col">Status</th>
                    <td className="text-end">
                      {modelDeploymentStatus
                        ? (
                          <Badge bg={modelDeploymentStatus.ready ? "success" : "warning"}>
                            <FontAwesomeIcon icon={modelDeploymentStatus.ready ? solid('cog') : solid('exclamation-triangle')} spin={modelDeploymentStatus.ready} />
                            <span className="ms-1">{modelDeploymentStatus.label}</span>
                          </Badge>
                        )
                        : <AppLoader className="py-5" justify="flex-end" size={20} />}
                    </td>
                  </tr>
                  {modelDeploymentStatus?.ready && modelMetadata && predictionLink && (
                    <>
                      <tr>
                        <th scope="col">Prediction</th>
                        <td className="text-end">
                          <Badge bg="light" className="border text-muted">
                            <span className="me-1">{predictionLink}</span>
                            <IconButton tooltip="Copy to Clipboard" className="p-1" onClick={copyToClipboard}>
                              <FontAwesomeIcon icon={solid('clipboard')} />
                            </IconButton>
                          </Badge>
                        </td>
                      </tr>
                      <tr>
                        <th scope="col">{modelLabels.singular} Metadata</th>
                        <td className="text-end">
                          <ScriptEditor value={modelMetadata} height={300} readOnly />
                          <small className="float-start text-muted">
                            <FontAwesomeIcon icon={solid("info-circle")} className="me-2" />
                            To interact with the deployed model you can feed the content of the "inputs" field above as body of the prediction request
                          </small>
                        </td>
                      </tr>
                    </>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {model.datasource
          ? (
            <Card className="item-detail-group mb-3">
              <div className="item-detail-highlight">
                <FontAwesomeIcon icon={solid('database')} fixedWidth />
                <h5 className="d-inline ms-2 mb-0">{modelLabels.singular} Source</h5>
              </div>

              <Card.Body>
                <Table className="mb-0">
                  <tbody>
                    <tr>
                      <th scope="col">Name</th>
                      <td className="text-end">
                        <Link to={`/datasources/${model.datasource.id}`} target="_blank" rel="noopener noreferrer">{model.datasource.name}</Link>
                      </td>
                    </tr>
                    <tr>
                      <th scope="col">Description</th>
                      <td className="text-end">{model.datasource.description}</td>
                    </tr>
                    <tr>
                      <th scope="col">Driver</th>
                      <td className="text-end">{model.datasource.driverType}</td>
                    </tr>
                    <tr>
                      <th scope="col">Host</th>
                      <td className="text-end">{model.datasource.host}</td>
                    </tr>
                    <tr>
                      <th scope="col">Port</th>
                      <td className="text-end">{model.datasource.port}</td>
                    </tr>
                    {model.datasource.type === "objectStorage" && setObjectType<ObjectStorageDatasource>(model.datasource) && (
                      <tr>
                        <th scope="col">Bucket</th>
                        <td className="text-end">
                          <FontAwesomeIcon icon={solid('bucket')} size="sm" />
                          <span className="ms-2">{model.datasource.bucket}</span>
                        </td>
                      </tr>
                    )}
                    {model.datasource.type === "database" && setObjectType<DatabaseDatasource>(model.datasource) && (
                      <tr>
                        <th scope="col">Database</th>
                        <td className="text-end">
                          <FontAwesomeIcon icon={solid('database')} size="sm" />
                          <span className="ms-2">{model.datasource.database}</span>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )
          : (
            <Alert variant="warning" className="py-2 mb-3">
              <FontAwesomeIcon icon={solid('exclamation-triangle')} />
              <span className="ms-2">{modelLabels.singular} data source not found.</span>
            </Alert>
          )
        }

        <Card className="item-detail-group mb-3">
          <div className="item-detail-highlight">
            <FontAwesomeIcon icon={solid('info-circle')} fixedWidth />
            <h5 className="d-inline ms-2 mb-0">{modelLabels.singular} Info</h5>
          </div>

          <Card.Body>
            <Table>
              <thead>
                <tr>
                  <th className="border-0 p-0" style={{ width: 200 }}></th>
                  <th className="border-0 p-0"></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <th scope="col">Location</th>
                  <td className="text-end">{model.location}</td>
                </tr>
                <tr>
                  <th scope="col">Format</th>
                  <td className="text-end">
                    {(model.format === "mlflow" && model.mlFlowModelUrl)
                      ? (
                        <a href={model.mlFlowModelUrl} target="_blank" rel="noopener noreferrer">
                          <Badge bg="light" className="border text-primary">
                            <FontAwesomeIcon icon={solid('link')} />
                            <span className="ms-2">MlFlow</span>
                          </Badge>
                        </a>
                      )
                      : <span>{model.format || 'N/A'}</span>
                    }
                  </td>
                </tr>
                {model.createdByBdaId && (
                  <tr>
                    <th scope="col">Created By {applicationLabels.singular}</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/bda-applications/${model.createdByBdaId}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('laptop-code')} />
                          <span className="ms-2">{model.createdByBdaId}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.createdByServiceId && (
                  <tr>
                    <th scope="col">Algorithm</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/services/${model.createdByServiceId}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('cubes')} />
                          <span className="ms-2">{model.algorithmName}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.trainingDataset && (
                  <tr>
                    <th scope="col">Training Dataset</th>
                    <td className="text-end">
                      <Link className="d-inline-block" to={`/datasets/${model.trainingDataset.id}`} target="_blank" rel="noopener noreferer">
                        <Badge bg="light" className="border text-primary">
                          <FontAwesomeIcon icon={solid('database')} />
                          <span className="ms-2">{model.trainingDataset.name}</span>
                        </Badge>
                      </Link>
                    </td>
                  </tr>
                )}
                {model.inputs && model.inputs !== "" && (
                  <tr>
                    <th scope="col">Input Columns</th>
                    <td className="text-end">
                      <div className="max-h-md">
                        {model.inputs.split(",").map(col => <Badge key={col} bg="light" className="border text-secondary me-1">{col}</Badge>)}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {model.properties && (
              <>
                <h6>Properties</h6>
                <Table className="mb-0">
                  <tbody>
                    {model.properties.map((wrapper, index) => {
                      const [key] = Object.keys(wrapper);
                      const value = wrapper[key];

                      return (
                        <tr key={index}>
                          <th scope="col">{key}</th>
                          <td className="text-end"><code>{value}</code></td>
                        </tr>
                      )
                    })}
                  </tbody>
                </Table>
              </>
            )}
          </Card.Body>
        </Card>

        {modelCard && (
          <Card className="item-detail-group mb-3">
            <div className="item-detail-highlight d-flex justify-content-between align-items-center">
              <div>
                <FontAwesomeIcon icon={solid('code')} fixedWidth />
                <h5 className="d-inline ms-2 mb-0">{modelLabels.singular} Card Info</h5>
                {modelCardFieldsMissing && (
                  <FontAwesomeIcon icon={solid('exclamation-triangle')} className="ms-2 text-warning" fixedWidth />
                )}
              </div>
              <AntButton size="small" type="text" onClick={handleModelCardDownload} icon={<FontAwesomeIcon icon={solid('download')} />}>
                Download {modelLabels.singular} Card
              </AntButton>
            </div>

            <Card.Body>
              {modelCardFieldsMissing && (
                <Alert variant="warning" className="py-2 mb-2">
                  <FontAwesomeIcon icon={solid('exclamation-triangle')} />
                  <span className="ms-2">This model is not compliant with the Hugging Face Model Card. Missing recommended fields:
                    {modelCard.missingFields.map((field: string, index: number) => (
                      <>
                        <div className="fw-bold d-inline text-capitalize ms-1">{field}</div>
                        {index < modelCard.missingFields.length - 1 && ','}
                      </>
                    ))}
                  </span>
                </Alert>
              )}
              <Table>
                <thead>
                  <tr>
                    <th className="border-0 p-0" style={{ width: 200 }}></th>
                    <th className="border-0 p-0"></th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(modelCard)
                    .filter(([key]) => !["markdown", "missingFields"].includes(key))
                    .filter(([_key, value]) => !!value)
                    .map(([key, value], index) => (
                      <tr key={index}>
                        <th scope="col">{key}</th>
                        <td className="text-end">{renderModelCardValue(key, value)}</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        )}

        {window.__env.DATASPACES_ENABLED && (
          <Card className="item-detail-group">
            <div className="item-detail-highlight">
              <FontAwesomeIcon icon={solid('store')} fixedWidth />
              <h5 className="d-inline ms-2 mb-0">Available at</h5>
            </div>

            <Card.Body className="p-0 pt-1 px-2">
              {!connectors && <AppLoader className="py-3" justify="center" size={60} />}

              {connectors?.length === 0
                ? (
                  <Alert variant="info" className="mt-2 p-2">
                    <FontAwesomeIcon icon={solid('info-circle')} />
                    <span className="ms-2">No offers found for this {modelLabels.singular}.</span>
                  </Alert>
                )
                : (
                  <Row>
                    {connectors?.map((connector) => (
                      <Col key={connector.id} className="px-2 mb-3" md={6} lg={4}>
                        <DataspaceConnectorItem className="h-100" connector={connector} actions={<></>} />
                      </Col>
                    ))}
                  </Row>
                )
              }
            </Card.Body>
          </Card>
        )}

        {model.markDown?.value && (
          <Card className="item-detail-group pb-3">
            <div data-color-mode="light">
              <MDEditor.Markdown className="px-2" source={model.markDown.value} />
            </div>
          </Card>
        )}
      </>
    )
  }

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{modelLabels.singular} {id} Detail</title>
      </Helmet>

      {renderAsyncContent()}

      <br />

      <Modal show={showDeleteModal} onHide={handleDeleteClose}>
        <Modal.Header closeButton>
          <Modal.Title>Delete {modelLabels.singular}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete {modelLabels.singular} <b>{model && model.name}</b>?</p>

          <Alert className="p-2" variant="warning">
            <FontAwesomeIcon icon={solid('exclamation-triangle')} />
            <span className="ms-2">Remember, this operation cannot be undone.</span>
          </Alert>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="light" size="sm" className="border" onClick={handleDeleteClose}>Close</Button>
          <Button variant="danger" size="sm" onClick={deleteModel}>Delete</Button>
        </Modal.Footer>
      </Modal>

      {/* <Modal show={showSettingsModal} onHide={handleSettingsClose}>
        <Modal.Header closeButton>
          <Modal.Title>{modelLabels.singular} Deployment Settings</Modal.Title>
        </Modal.Header>
        <Form form={form} {...formLayout} onFinish={startModelDeployment}>
          <Modal.Body>
            <Form.Item
              label="Replicas"
              name="replicasNumber"
              extra="Defaults to 1"
              rules={[
                { min: 1, type: 'number', message: 'Replicas must be greater than or equal to 1' },
                { required: true, message: 'Replicas is required' },
              ]}>
              <Input placeholder="Number of replicas" type="number" min={1} />
            </Form.Item>
            <Form.Item
              label="CPU Limit"
              name="cpuLimit"
              extra="Defaults to 200m"
              rules={[{ required: true, message: 'CPU Limit is required' }]}
            >
              <Input placeholder="Limit on the CPU" type="number" suffix="m" />
            </Form.Item>
            <Form.Item
              label="Memory Limit"
              name="memoryLimit"
              extra="Defaults to 256Mi"
              rules={[{ required: true, message: 'Memory Limit is required' }]}
            >
              <Input placeholder="Limit on RAM Memory" type="number" suffix="Mi" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="justify-content-between">
            <Button variant="light" size="sm" className="border" type="button" onClick={handleSettingsClose}>Close</Button>
            <Button variant="success" size="sm" type="submit">
              <FontAwesomeIcon icon={regular('play-circle')} />
              <span className="ms-2">Start</span>
            </Button>
          </Modal.Footer>
        </Form>
      </Modal> */}

      <Modal show={showProjectsModal} onHide={handleProjectsModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>{modelLabels.singular} Projects</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <AssetProjectsEditor assetId={+id!} assetType="model" assetLabel={modelLabels.singular} />
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <Button variant="light" className="border" onClick={handleProjectsModalClose} size="sm">Cancel</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
